export interface AuthResponse {
  token: string
  refreshToken: string
  locationId: string
  userId: string
  contactId: string
  externalUserId: string
  sessionUniqueKey: string
  multipleUsers?: boolean
  isPasswordVerified?: boolean
  passwordResetToken?: string
  email?: string
  migrationStatus?: MigrationStatus
}

/** Returned by magic-link login when the token is expired and BE sent a login OTP. */
export interface MagicLinkOtpHandoffResponse {
  requiresOtp: true
  email: string
  otpToken: string
}

export type MagicLinkLoginResponse = AuthResponse | MagicLinkOtpHandoffResponse

export function isMagicLinkOtpHandoff(data: MagicLinkLoginResponse | null | undefined): data is MagicLinkOtpHandoffResponse {
  return Boolean(data && 'requiresOtp' in data && data.requiresOtp === true)
}

/**
 * Server migration lifecycle. Drives the post-login gate:
 * - CREATED            → user must set a password (Expired Password screen).
 * - PASSWORD_CONFIRMED → user must confirm their profile (Profile Confirmation screen).
 * - ONBOARDED          → profile confirmed; continue normal flow.
 * - MIGRATED           → fully migrated; no gate.
 */
export enum MigrationStatus {
  CREATED = 'CREATED',
  PASSWORD_CONFIRMED = 'PASSWORD_CONFIRMED',
  ONBOARDED = 'ONBOARDED',
  MIGRATED = 'MIGRATED',
}

/**
 * localStorage key used by `MigrationUtility` to persist mid-migration session state.
 * Kept here so consumers (utility, tests, gate components) share a single source of truth.
 */
export const MIGRATION_STORAGE_KEY = 'user_migration_data'

export interface MigrationSessionData {
  migrationStatus: MigrationStatus
  migrationUserId?: string
  email?: string
  contactId?: string
  passwordResetToken?: string
  preLogin?: boolean
}

export interface DeviceInfo {
  deviceName: string
  deviceId: string
  deviceType: 'mobile' | 'desktop'
  domainName: string
  ipAddress: string
}

// Auth Service Payload Interfaces
export interface SignUpPayload {
  email: string
  password: string
  username: string
  locationId: string
  otp?: number
  redirectUrl?: string
  captchaToken?: string
  referredBy?: string
  signupPageUrl?: string
  otpToken?: string
}

export interface LoginWithEmailPasswordPayload {
  email: string
  password: string
  locationId: string
  userId?: string
  redirectUrl?: string
  captchaToken?: string
}

export interface LoginWithSecureCodePayload {
  email: string
  otp?: number
  token?: string
  locationId: string
  redirectUrl?: string
  action?: string
  fullName?: string
  captchaToken?: string
}

export interface SendOtpPayload {
  email: string
  locationId: string
  fullName?: string
  captchaToken?: string
  action: 'login' | 'signup'
  type?: string
}

export interface LoginWithSocialIDPayload {
  authProvider: string
  idToken: string
  locationId: string
  email?: string
  userId?: string
  fullName?: string
  domainName?: string
  redirectUrl?: string
  referredBy?: string
  signupPageUrl?: string
}

export interface LoginWithLoginCodePayload {
  // Optional: backend resolves the user from loginCode alone when email is absent
  email?: string
  loginCode: string
  locationId: string
  domainName: string
  redirectUrl?: string | null
}

export interface LoginWithMagicLinkPayload {
  // Optional: backend resolves the user from the magic-link token alone when email is absent
  email?: string
  token: string
  locationId: string
  domainName: string
  redirectUrl?: string | null
  /** Clientportal opt-in: expired magic-link login returns OTP handoff instead of 400. */
  otpHandoffOnExpire?: boolean
}

export interface LoginWithSessionKeyPayload {
  // Optional: backend resolves the user from sessionKey alone when email is absent
  email?: string
  sessionKey: string
  locationId: string
  domainName: string
  redirectUrl?: string | null
}

//use isauthv2enabled and assign v1/v2 to this constant. Currently since we support only v2, use v2 as the default.
export const MAGIC_LINK_REQUEST_SOURCE_CLIENTPORTAL_PREVIEW_V2 = 'clientportal_preview_v2' as const

export type MagicLinkRequestSource = typeof MAGIC_LINK_REQUEST_SOURCE_CLIENTPORTAL_PREVIEW_V2

/** Required POST body for `/tokens/request-magic-link`. */
export interface RequestMagicLinkPayload {
  token: string
  locationId: string
  source: MagicLinkRequestSource
}

export interface SetPasswordPayload {
  passwordResetToken: string
  contactId: string
  newPassword: string
  confirmPassword: string
  locationId: string
  captchaToken?: string
}

export interface SetNewPasswordPayload {
  locationId: string
  emailId: string
  newPassword: string
  confirmPassword: string
  loginCode?: string
  token?: string
}

export interface ForgotPasswordPayload {
  email: string
  locationId: string
  userId?: string
  captchaToken?: string
}

export interface ResetPasswordPayload {
  userId: string
  token: string
  password: string
  locationId: string
  revokeAllSessions?: boolean
}

export interface ValidateResetLinkPayload {
  userId: string
  token: string
  locationId: string
}

export interface RefreshTokenPayload {
  refreshToken: string
  locationId: string
}

export interface RefreshTokenResponse {
  token: string
  refreshToken: string
  sessionUniqueKey?: string
  sessionUniqueId?: string
}

export interface UpdateSessionPayload {
  locationId: string
  sessionUniqueKey: string
  isV2: boolean
  pushToken?: string
  brandedAppId?: string
}

export interface CheckEmailPayload {
  email: string
  locationId: string
}

export interface CheckEmailResponse {
  success?: boolean
  exists?: boolean
  user?: {
    _id: string
    migrationStatus?: MigrationStatus
  }
}

export interface GetMigrationStatusResponse {
  migrationStatus: MigrationStatus
  migrationUserId?: string
}

export interface CheckPasswordSetPayload {
  locationId: string
  email?: string
  userId?: string
  loginCode?: string | null
  token?: string | null
}

export interface CheckPasswordSetResponse {
  isPasswordSet: boolean
  email?: string
  externalUserId?: string
  isTokenExpired?: boolean
  passwordResetToken?: string
  contactId?: string
}

// UseAuth Composable Payload Interfaces
export interface UseAuthSignUpPayload {
  email: string
  password: string
  username: string
  otp: number
  redirectUrl?: string
  captchaToken?: string
  referredBy?: string
  signupPageUrl?: string
  otpToken?: string
}

export interface UseAuthSendOtpPayload {
  email: string
  action: 'login' | 'signup'
  captchaToken?: string
  type?: string
  fullName?: string
}

export interface UseAuthSetPasswordPayload {
  passwordResetToken: string
  contactId: string
  newPassword: string
  confirmPassword: string
  captchaToken?: string
}

/**
 * Composable-facing payload for the pre-login set-password endpoint. The
 * composable injects `locationId` from `useLocationStore`, so callers only
 * need to supply the form values and the URL credentials.
 */
export interface UseAuthSetNewPasswordPayload {
  emailId: string
  newPassword: string
  confirmPassword: string
  loginCode?: string
  token?: string
}

/** CPForm ref shape used by forgot-password (validate + restoreValidation). */
export interface AuthForgotPasswordFormRef {
  getForm: () => {
    validate: () => Promise<void>
    restoreValidation: () => Promise<void>
  }
}

export interface ScreenChangeData {
  page?: string
  email?: string
  username?: string
  password?: string
  otpToken?: string
  otp?: string
  action?: 'login' | 'signup'
  multiUser?: boolean
  referrerUser?: {
    email: string
    name: string
    _id?: string
  }
  passwordResetToken?: string
  contactId?: string
}

export enum Screens {
  LOGIN = 'LOGIN',
  LOGIN_WITH_EMAIL = 'LOGIN_WITH_EMAIL',
  LOGIN_WITH_SECURE_CODE = 'LOGIN_WITH_SECURE_CODE',
  VERIFY_SECURE_CODE = 'VERIFY_SECURE_CODE',
  SELECT_USER_MULTI_USER = 'SELECT_USER_MULTI_USER',
  SET_PASSWORD = 'SET_PASSWORD',
  FORGOT_PASSWORD = 'FORGOT_PASSWORD',
  PROCEED_TO_LOGIN = 'PROCEED_TO_LOGIN',
  SIGN_UP = 'SIGN_UP',
  EXPIRED_PASSWORD = 'EXPIRED_PASSWORD',
  PROFILE_CONFIRMATION = 'PROFILE_CONFIRMATION',
}

/** Screens for the standalone reset-password flow (validate link → form → expired/success). */
export enum ResetPasswordScreen {
  VALIDATING,
  FORM,
  EXPIRED,
  SUCCESS,
}

export enum LoginEvents {
  CHANGE_SCREEN = 'changeScreen',
  LOGIN_CALLBACK = 'loginCallback',
  LOGIN_SUCCESSFUL = 'LOGIN_SUCCESSFUL',
  /** Emitted by a migration-gate component (e.g. ProfileConfirmation) once its gate clears. */
  MIGRATION_GATE_CLEARED = 'MIGRATION_GATE_CLEARED',
}

export enum AuthProvider {
  GOOGLE = 'Google',
}

export interface GoogleCredentialResponse {
  credential?: string
}

export interface LoginCallbackPayload extends AuthResponse {
  eventType: string
}

/**
 * IAM v2 courses admin-preview token exchange payload (mirrors membership-hmi-preview PR #1321
 * `signInAsAdminV2`). The `v2.`-prefixed token must be sent as both the body `token` and the
 * `Authorization: Bearer` header for the IAM gateway to authorize it.
 */
export interface LoginWithAdminTokenV2Payload {
  agencyToken: string
  locationId: string
  /**
   * Agency company id from the builder preview URL (`company_id`). Forwarded to membership-backend
   * so it can fall back to an agency-user lookup by email + company when the CP contact has no
   * mapped agency user id (mirrors membership-hmi-preview PR #1329). Omitted from the body when absent.
   */
  companyId?: string | null
}

/**
 * Response from `POST /user/v2/login-token`. `role: 'admin'` marks this as admin-preview (vs.
 * learner) session; no Firebase sign-in occurs on this path.
 */
export interface AdminV2PreviewSession {
  accessToken: string
  role: string
  locationId: string
  tokenExpirationTime?: number | string
}
